import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { CartItem } from "../Types/CartItemType";
import { toast } from "react-toastify";

const CART_STORAGE_KEY = "essa-cart";

export const getCartItemKey = (item: Pick<CartItem, "id" | "size">) =>
  item.size ? `${item.id}:${item.size}` : item.id;

type CartContextType = {
  cartItems: CartItem[];
  addToCart: (product: CartItem, options?: { message?: string; silent?: boolean }) => void;
  removeFromCart: (key: string) => void;
  clearCart: () => void;
  getTotal: () => number;
  increaseQuantity: (key: string) => void;
  decreaseQuantity: (key: string) => void;
};

const CartContext = createContext<CartContextType>({} as CartContextType);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch {
      // ignore storage errors
    }
  }, [cartItems]);

  const addToCart = (product: CartItem, options?: { message?: string; silent?: boolean }) => {
    const key = getCartItemKey(product);
    const toastId = `cart-add-${key}`;
    const message = options?.message ?? `${product.name} ${"adicionado ao carrinho!"}`;
    setCartItems(prev => {
      const existing = prev.find(item => getCartItemKey(item) === key);
      if (existing) {
        if (!options?.silent && !toast.isActive(toastId)) {
          toast.info("Este produto já foi adicionado ao carrinho!", { toastId });
        }
        return prev;
      }
      if (!options?.silent && !toast.isActive(toastId)) {
        toast.success(message, { toastId });
      }
      return [...prev, product];
    });
  };

  const removeFromCart = (key: string) => {
    setCartItems(prev => prev.filter(item => getCartItemKey(item) !== key));
  };

  const clearCart = () => setCartItems([]);

  const getTotal = () =>
    cartItems.reduce((total, item) => total + item.price * item.quantity, 0);

  const increaseQuantity = (key: string) => {
    setCartItems(prev =>
      prev.map(item =>
        getCartItemKey(item) === key &&
        item.quantity < (item.isMadeToOrder && item.stockQuantity <= 0 ? 300 : item.stockQuantity)
          ? { ...item, quantity: item.quantity + 1 }
          : item
      )
    );
  };

  const decreaseQuantity = (key: string) => {
    setCartItems(prev =>
      prev.map(item =>
        getCartItemKey(item) === key && item.quantity > 1
          ? { ...item, quantity: item.quantity - 1 }
          : item
      )
    );
  };

  return (
    <CartContext.Provider
      value={{ 
        cartItems, 
        addToCart, 
        removeFromCart,
        clearCart,
        getTotal,
        increaseQuantity,
        decreaseQuantity
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
